/*
* Report page
*/

import * as _ from 'lodash';
import * as React from 'react';
import { Row } from 'react-bootstrap';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { BootstrapColXl as Col } from '../../../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../../../common/formatters';
import ApiEndpoints from '../../../config/apiendpoints';
import { isLevel3Report } from '../../../order/utils';
import { getComponentDisplayId, getComponentSortFields } from '../../component-sorting';
import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, estCharactersPerLine = 0, pageTitle = '', className = '', maxTableRowsPerFirstPage = 0, maxTableRowsPerPage = 0, pageConfiguration, pageCount, reportData, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.componentEvaluation;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageHeaderFooter, sectionName, registerFunc, refFunc, scrollToFunc);

    const chunkSizeFirstPage = (maxTableRowsPerFirstPage > 0) ? maxTableRowsPerFirstPage : _.get(pageConfiguration, 'maxTableRowsPerFirstPage', 15);
    const chunkSizeOtherPages = (maxTableRowsPerPage > 0) ? maxTableRowsPerPage : _.get(pageConfiguration, 'maxTableRowsPerPage', 15);
    const charactersPerLine = (estCharactersPerLine > 0) ? estCharactersPerLine : _.get(pageConfiguration, 'estCharactersPerLine', 100);

    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData } = reportData;
    const reportOptionsGroupComponents = Formatters.getBool(currentOrderChart, 'groupComponents');

    // Helpers
    const transformDataItems = (dataItems: any[]) => {
        const transformed = _(dataItems)
            .map((item, ind) => {
                const g = _.get(item, 'subgroup', '');
                const componentSortFields = getComponentSortFields(item, ind);
                // const sg = _.find(association.inventorySubgroups, ['groupCode', g]);
                const sg = (_.find(association.inventorySubgroups, ['groupCode', g])) ? _.find(association.inventorySubgroups, ['groupCode', g]) : _.find(association.inventorySubgroups, ['groupCode', _.toString(_.parseInt(g))]);

                const result = _.merge(
                    {},
                    {
                        subgroup: _.get(sg, 'title', ''),
                        groupCode: sg,
                    },
                    componentSortFields,
                    _.pick(item, [
                        'bestCost',
                        'bestExplanation',
                        'componentDecimal', // 20240711 added
                        'componentId',
                        'componentName',
                        'costSource',
                        'evaluation',
                        'generalNote',
                        'reportNotes',
                        'location',
                        'pictures',
                        'printNote',
                        'quantity',
                        'remainingUsefulLife',
                        'reportNotes',
                        'usefulLife',
                        'worstCost',
                        'worstExplanation',
                    ])
                );

                return result;
            })
            .flatten()
            .value();

        const transformedSort = (Formatters.getBool(currentOrderChart, 'groupComponentsPictureSection')) ?
            _.orderBy(transformed, [
                c => Formatters.getStr(c.groupCode, 'groupCode'),
                c => _.get(c, '__componentSortBase', 0),
                c => _.get(c, '__componentSortDecimal', -1),
                c => _.get(c, '__componentSortName', ''),
                c => _.get(c, '__componentSortStable', ''),
            ], ['asc', 'asc', 'asc', 'asc', 'asc']) :
            _.orderBy(transformed, [
                c => _.get(c, '__componentSortBase', 0),
                c => _.get(c, '__componentSortDecimal', -1),
                c => _.get(c, '__componentSortName', ''),
                c => _.get(c, '__componentSortStable', ''),
            ], ['asc', 'asc', 'asc', 'asc']);

        return transformedSort;
    };

    const buildHeader = (item, isFirstChunkPage, isLastChunkPage) => {
        const componentDisplayId = getComponentDisplayId(item);

        const result = (
            <div className="pdf-page-component-evaluation-header">
                <ul className="list-inline">
                    <li>Comp #</li>
                    <li className="text-right pdf-page-component-evaluation-header-component-id">{componentDisplayId}</li>
                    <li>{_.get(item, 'componentName', '')}</li>
                    <li>{((isLastChunkPage && !isFirstChunkPage) ? 'cont.' : '')}</li>
                </ul>
            </div>
        );

        return result;
    };

    const HorizontalList = (props) => {
        const { classNameTitle, classNameValue, children, ...otherProps } = props;

        return (
            <ReportComponents.HorizontalDescriptionList
                classNameTitle={`${classNameTitle} pdf-page-component-evaluation-hdl-title`}
                classNameValue={`${classNameValue} pdf-page-component-evaluation-hdl-value`}
                {...otherProps}
            >
                {...children}
            </ReportComponents.HorizontalDescriptionList>
        );
    };

    const CostExplanation = (props) => {
        const { classNameValue, children, ...otherProps } = props;

        return (
            <ReportComponents.CostExplanation
                classNameValue={`${classNameValue} pdf-page-component-evaluation-hdl-value-cost-explanation`}
                {...otherProps}
            >
                {...children}
            </ReportComponents.CostExplanation>
        );
    };

    const buildDetails = (item) => {
        const result = (
            <div className="pdf-page-component-evaluation-details">
                {reportOptionsGroupComponents && <HorizontalList classNameContainer="pdf-page-component-evaluation-details-subgroup" title="Subgroup" value={_.get(item, 'subgroup', '')} />}

                <HorizontalList title="Location" value={_.get(item, 'location', '')} />

                <HorizontalList title="Quantity" value={_.get(item, 'quantity', '')} />

                <HorizontalList classNameContainer="dl-horizontal dl-inline" title="Life Expectancy" value={_.get(item, 'usefulLife', '')} />
                <HorizontalList classNameContainer="dl-horizontal dl-inline" title="Remaining Life" value={_.get(item, 'remainingUsefulLife', 0)} />

                <HorizontalList title="Best Cost" value={(<ReportComponents.Currency value={_.get(item, 'bestCost', '')} decimals={2} />)} />
                <ReportComponents.CostExplanation explanation={_.get(item, 'bestExplanation', '')} />

                <HorizontalList title="Worst Cost" value={(<ReportComponents.Currency value={_.get(item, 'worstCost', '')} decimals={2} />)} />
                <ReportComponents.CostExplanation explanation={_.get(item, 'worstExplanation', '')} />

                <HorizontalList title="Source of Information" value={_.get(item, 'costSource', '')} />
            </div>
        );

        return result;
    };

    const buildObservations = (text: string, isLastChunkPage: boolean) => {
        const result = (text) ? (
            <div className="pdf-page-component-evaluation-observations">
                <HorizontalList
                    title={(isLastChunkPage) ? 'Observations' : 'Observations Continued on Next Page'}
                    value={(isLastChunkPage) ? text : ''}
                    classNameValue="pdf-page-component-evaluation-hdl-value pdf-page-component-evaluation-hdl-value-observations"
                />
            </div>
        ) : (<div />);

        return result;
    };

    const buildGeneralNotes = (notes: string = '', title: string = '', classNameValue: string = 'pdf-page-component-evaluation-hdl-value pdf-page-component-evaluation-hdl-value-general-notes') => {
        if (!_.trim(notes)) { return (<div />); }

        const result = (
            <div className="pdf-page-component-evaluation-general-notes">
                <HorizontalList
                    title={title}
                    value={(<pre className="pdf-page-component-evaluation-general-notes-pre">{_.trim(notes)}</pre>)}
                    classNameValue={classNameValue}
                />
            </div>
        );

        return result;
    };

    const buildReportNotes = (notes: Array<any> = [], title: string = '', classNameValue: string = 'pdf-page-component-evaluation-hdl-value pdf-page-component-evaluation-hdl-value-report-notes') => {
        if (!_.size(notes)) { return (<div />); }

        const text = _.map(notes, (o) => {
            return Formatters.getStr(o, 'notes');
        }).join('. ');

        const result = (
            <div className="pdf-page-component-evaluation-report-notes">
                <HorizontalList
                    title={title}
                    value={text}
                    classNameValue={classNameValue}
                />
            </div>
        );

        return result;
    };

    const buildPictureLazy = ({ imgSrc, key, description = '', colXs = 6, classNameImg = 'img-rounded img-responsive association-inventory-detail-photo' }) => {
        const result = (
            <Col key={key} xs={colXs}>
                <div className="center-block">
                    <LazyLoadImage
                        className={classNameImg}
                        src={imgSrc}
                        alt={description}
                        title={description}
                    />
                </div>
            </Col>
        );

        return result;
    };

    const buildPicture = ({ imgSrc, key, description = '', colXs = 6, classNameImg = 'img-rounded img-responsive association-inventory-detail-photo' }) => {
        const result = (
            <Col key={key} xs={colXs}>
                <div className="center-block">
                    <img
                        className={classNameImg}
                        src={imgSrc}
                        alt={description}
                        title={description}
                    />
                </div>
            </Col>
        );

        return result;
    };

    const buildPictures = (item) => {
        const itemPictures = _.without(_.get(item, 'pictures', []), '');

        const pictures = _(itemPictures)
            .map((picPath, picInd) => {
                const picResult = {
                    // 20200313 was imgSrc: ApiEndpoints.getAppOrderPictureUrl(picPath, false, true),
                    imgSrc: ApiEndpoints.getInventoryPictureUrl(picPath, true, 'w_400,f_auto/'),
                    description: `Picture ${picInd + 1}: ${_.get(item, 'componenentName', '')}`,
                    key: `${item.componentId}-picture-${picInd + 1}`,
                };

                return picResult;
            })
            .value();

        const result = isLevel3Report(currentOrder) ? <div /> : (
            <div className="pdf-page-component-evaluation-pictures">
                <Row>
                    {
                        pictures.map((picturesProps, ind) => buildPicture({ ...picturesProps }))
                    }
                </Row>
            </div>
        );

        return result;
    };

    const chunkString = (str: string, len: number) => {
        const size = Math.ceil(str.length / len);
        let ret = new Array(size);
        let offset;

        for (let i = 0; i < size; i++) {
            offset = i * len;
            ret[i] = str.substring(offset, offset + len);
        }

        return ret;
    };

    const getChunkedEvaluation = (evaluation: string, componentId: number = 0) => {
        const evaluationLen = _.size(evaluation);
        const approxTotalTextLines = (evaluationLen && charactersPerLine) ? Math.round(evaluationLen / charactersPerLine) : 1;

        const chunkedSizeCharactersFirstPage = (chunkSizeFirstPage * charactersPerLine);
        const chunkedSizeCharactersOtherPages = (chunkSizeOtherPages * charactersPerLine);

        if (approxTotalTextLines > chunkSizeFirstPage) {
            // Too long, chunk it
            const left = evaluation.substr(0, chunkedSizeCharactersFirstPage);
            const right = evaluation.substr(chunkedSizeCharactersFirstPage);

            const ch = chunkString(right, chunkedSizeCharactersOtherPages);

            const ret = _.concat([left], ch);

            return ret;
        } else {
            return [evaluation];
        }
    };

    const getChunkedItem = (evaluation: string, generalNote: string, componentId: number = 0) => {
        const evaluationLen = _.size(evaluation);
        const generalNoteLen = _.size(generalNote);
        const approxTotalNoteTextLines = (generalNoteLen) ? Math.round(generalNoteLen / (charactersPerLine / 2)) : 0;
        const approxTotalEvalTextLines = (evaluationLen && charactersPerLine) ? Math.round(evaluationLen / charactersPerLine) : 1;

        const chunkedSizeCharactersFirstPage = (chunkSizeFirstPage * charactersPerLine);
        const chunkedSizeCharactersOtherPages = (chunkSizeOtherPages * charactersPerLine);

        // general note too big? chunk and push pics/eval to second page
        // eval text too big? chunk and push pics/eval to second page

        if (approxTotalEvalTextLines + approxTotalNoteTextLines > chunkSizeFirstPage) {
            const ret = _.concat(evaluation, [evaluation]);
            return ret;
        } else {
            return [evaluation];
        }
    };

    // CHUNK DATA/TABLES TO PAGES
    const pages = _(inventory)
        .thru(transformDataItems)
        .map((item, itemInd, itemInputArr) => {
            const evaluation = _.get(item, 'evaluation', '');
            const generalNote = _.get(item, 'generalNote', '');

            // const chunkedEvaluation = getChunkedEvaluation(evaluation, _.get(item, 'componentId', 0));
            const chunks = getChunkedItem(evaluation, generalNote, _.get(item, 'componentId', 0));

            return (chunks)
                .map((chunkItem, chunkInd, chunkInputArr) => {
                    const isFirstChunkPage = (chunkInd === 0);
                    const isLastChunkPage = ((chunkInd + 1) === _.size(chunkInputArr));

                    const chunkItemText = chunkItem;

                    const header = buildHeader(item, isFirstChunkPage, isLastChunkPage);
                    const details = (isFirstChunkPage) ? buildDetails(item) : <div />;

                    // const generalNotes = (_.size(item.generalNotes)) ? buildGeneralNotes(_.get(item, 'generalNotes', []), 'General Notes') : (<div />);
                    const generalNotes = (isFirstChunkPage) ? buildGeneralNotes(_.get(item, 'generalNote', ''), 'General Notes') : <div />;
                    const reportNotes = (item.printNote && _.size(item.reportNotes)) ? buildReportNotes(_.get(item, 'reportNotes', []), 'Report Notes') : (<div />);
                    const observations = buildObservations(chunkItemText, isLastChunkPage);
                    const pictures = (isLastChunkPage) ? buildPictures(item) : (<div />);

                    const page = (
                        <div key={`${item.componentId}-component-evaluation-${chunkInd}`}>
                            <ReportPageWithRegister
                                className={className}
                                footerData={reportData}
                                pageIndex={Formatters.getStr(item, 'componentName', '', true) + ' ' + Formatters.getStr(item, 'subgroup', '', true) + ' ' + ((isLastChunkPage && !isFirstChunkPage) ? 'cont.' : '')}
                                showFooter={true}
                                title={pageTitle}
                                totalPageCount={pageCount}
                            >
                                {header}

                                <div className="pdf-page-component-evaluation-info">
                                    <Row>
                                        <Col xs={6}>
                                            {details}
                                        </Col>
                                        <Col xs={6}>
                                            {generalNotes}
                                        </Col>
                                    </Row>

                                    {/* {reportNotes} */}

                                    {observations}

                                    {pictures}
                                </div>
                            </ReportPageWithRegister>
                        </div>
                    );

                    return page;
                });
        })
        .value();

    return (
        <div>
            {pages}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-component-evaluation.tsx